import React, { useState, useMemo  } from 'react';
import { Modal, Alert, Tree, Switch, Typography, Button, Space, Radio } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import './styles.scss';
import { accountActions } from '../../redux/actions';
import Localization from '../../utils/localization';
import AccountPicker from '../AccountPicker';

const { Text } = Typography;

const isPinnableAccount = (row) =>
    row?.account_id != null &&
    row.row_type !== 'separator' &&
    row.separator_id == null &&
    row.associated_account_id == null;

const accountLabel = (account) =>
    account.name ? `${account.account_number} — ${account.name}` : account.account_number;

const hasSubAccounts = (account) =>
    (account?.children ?? []).some(isPinnableAccount);

/**
 * Builds antd Tree nodes for the sub-accounts of the chosen account.
 * Sub-accounts already on the tab are shown but cannot be ticked.
 */
const buildChildNodes = (account, existingAccountIds) =>
    (account?.children ?? []).filter(isPinnableAccount).map((child) => {
        const alreadyOnTab = existingAccountIds.has(child.account_id);
        return {
            key: child.account_id,
            title: alreadyOnTab
                ? `${accountLabel(child)} (already on this tab)`
                : accountLabel(child),
            disableCheckbox: alreadyOnTab,
            children: buildChildNodes(child, existingAccountIds),
        };
    });

const collectKeys = (nodes, out = []) => {
    nodes.forEach((node) => {
        if (!node.disableCheckbox) out.push(node.key);
        collectKeys(node.children, out);
    });
    return out;
};

const collectDescendantIds = (account, out = []) => {
    (account?.children ?? []).filter(isPinnableAccount).forEach((child) => {
        out.push(child.account_id);
        collectDescendantIds(child, out);
    });
    return out;
};

const FavoriteAccount = ({
    accountStructure,
    groupId,
    financialGroupType,
    show,
    handleClose,
}) => {
    const dispatch = useDispatch();
    const creatingCustomTabAccount = useSelector(
        (state) => state.accounts.creatingCustomTabAccount,
    );

    const [selectedAccounts, setSelectedAccounts] = useState([]);
    // One account: the sub-accounts ticked in the tree.
    const [checkedChildIds, setCheckedChildIds] = useState([]);
    // Several accounts: account_id -> true when all its sub-accounts are included.
    const [includeAllById, setIncludeAllById] = useState({});
    const [autoAddChildren, setAutoAddChildren] = useState(true);
    const [alertText, setAlertText] = useState('');

    const isMultiple = selectedAccounts.length > 1;
    const singleAccount = selectedAccounts.length === 1 ? selectedAccounts[0] : null;

    const existingAccountIds = useMemo(() => {
        const ids = new Set();
        const tab = accountStructure?.custom?.find(
                (t) => t.financial_group_type === financialGroupType,
            );
        const walk = (arr) => {
            arr?.forEach((a) => {
                if (a.account_id != null) ids.add(a.account_id);
                if (a.children) walk(a.children);
            });
        };
        walk(tab?.custom_tab_accounts);
        return ids;
    }, [accountStructure, financialGroupType]);

    const childNodes = useMemo(
        () => buildChildNodes(singleAccount, existingAccountIds),
        [singleAccount, existingAccountIds],
    );
    const selectableChildIds = useMemo(() => collectKeys(childNodes), [childNodes]);

    /*
     * With several accounts, an account that sits under another selected
     * account set to "All" is already included through that account.
     */
    const includedVia = useMemo(() => {
        const result = new Map();
        selectedAccounts.forEach((ancestor) => {
            if (!includeAllById[ancestor.account_id]) return;
            const descendants = new Set(collectDescendantIds(ancestor));
            selectedAccounts.forEach((account) => {
                if (descendants.has(account.account_id) && !result.has(account.account_id)) {
                    result.set(account.account_id, ancestor);
                }
            });
        });
        return result;
    }, [selectedAccounts, includeAllById]);

    const reset = () => {
        setSelectedAccounts([]);
        setCheckedChildIds([]);
        setIncludeAllById({});
        setAutoAddChildren(true);
        setAlertText('');
    };

    const handleOk = () => {
        if (selectedAccounts.length === 0) {
            setAlertText('Please select an account');
            return;
        }
        const alreadyOnTab = selectedAccounts.filter((a) => existingAccountIds.has(a.account_id));
        if (alreadyOnTab.length > 0) {
            setAlertText(
                alreadyOnTab.length === 1
                    ? `${alreadyOnTab[0].account_number} is already added to this tab`
                    : `${alreadyOnTab.map((a) => a.account_number).join(', ')} are already added to this tab`,
            );
            return;
        }

        const pins = isMultiple
            ? selectedAccounts
                .filter((account) => !includedVia.has(account.account_id))
                .map((account) => ({
                  account_id: account.account_id,
                  childAccountIds: includeAllById[account.account_id]
                      ? collectDescendantIds(account).filter((id) => !existingAccountIds.has(id))
                      : [],
              }))
            : [{ account_id: singleAccount.account_id, childAccountIds: checkedChildIds }];

        if (!creatingCustomTabAccount) {
            dispatch(
                accountActions.createCustomTabAccount(
                    groupId,
                    financialGroupType,
                    accountStructure,
                    { pins, autoAddChildren },
                ),
            );
        }
        reset();
        handleClose();
    };

    const handleCancel = () => {
        reset();
        handleClose();
    };

    const renderSingleChildren = () => (
        <div className="favorite-account-children">
            <div className="favorite-account-children-header">
                <Text strong>Sub-accounts to include</Text>
                <Space size={0}>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => setCheckedChildIds(selectableChildIds)}
                    >
                        All
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => setCheckedChildIds([])}
                    >
                        None
                    </Button>
                </Space>
            </div>
            <Tree
                checkable
                checkStrictly
                selectable={false}
                defaultExpandAll
                treeData={childNodes}
                checkedKeys={checkedChildIds}
                onCheck={({ checked }) => setCheckedChildIds(checked)}
                height={260}
            />
            <Text type="secondary" className="favorite-account-hint">
                Sub-accounts you leave out are still included in{' '}
                {singleAccount.account_number}'s total. The tab shows the difference
                as an "Other" line.
            </Text>
        </div>
    );

    const renderMultipleChildren = () => {
        const parents = selectedAccounts.filter(hasSubAccounts);
        if (parents.length === 0) return null;

        return (
            <div className="favorite-account-children">
                <div className="favorite-account-children-header">
                    <Text strong>Sub-accounts to include</Text>
                </div>
                {parents.map((account) => {
                    const via = includedVia.get(account.account_id);
                    return (
                        <div key={account.account_id} className="favorite-account-parent-row">
                            <Text ellipsis className="favorite-account-parent-label">
                                {accountLabel(account)}
                            </Text>
                            {via ? (
                                <Text type="secondary">Included with {via.account_number}</Text>
                            ) : (
                                <Radio.Group
                                    size="small"
                                    optionType="button"
                                    value={includeAllById[account.account_id] ? 'all' : 'none'}
                                    onChange={(e) =>
                                        setIncludeAllById((prev) => ({
                                            ...prev,
                                            [account.account_id]: e.target.value === 'all',
                                        }))
                                    }
                                    options={[
                                        { label: 'None', value: 'none' },
                                        { label: 'All', value: 'all' },
                                    ]}
                                />
                            )}
                        </div>
                    );
                })}
                <Text type="secondary" className="favorite-account-hint">
                    "All" includes every level of sub-accounts below the account.
                </Text>
            </div>
        );
    };

    return (
        <Modal
            bodyStyle={{ display: 'flex', flexDirection: 'column' }}
            title={Localization.accountStructure.pinNewAccount}
            visible={show}
            onOk={handleOk}
            okButtonProps={{ disabled: selectedAccounts.length === 0 }}
            confirmLoading={false}
            onCancel={handleCancel}
        >
            <AccountPicker
                multiple
                accountStructure={accountStructure}
                value={selectedAccounts.map((a) => a.account_id)}
                onChange={(accounts) => {
                    setSelectedAccounts(accounts);
                    setCheckedChildIds([]);
                    setAlertText('');
                }}
                style={{ width: '100%' }}
            />

            {singleAccount && childNodes.length > 0 && renderSingleChildren()}
            {isMultiple && renderMultipleChildren()}

            {selectedAccounts.length > 0 && (
                <label className="favorite-account-auto-add">
                    <Switch
                        size="small"
                        checked={autoAddChildren}
                        onChange={setAutoAddChildren}
                    />
                    <span>Add new sub-accounts automatically</span>
                </label>
            )}

            {alertText !== '' && (
                <Alert message={alertText} type="error" showIcon style={{ marginTop: 12 }} />
            )}
        </Modal>
    );
};

export default FavoriteAccount;
